import axios from 'axios';
import debounce from 'lodash.debounce';

import { PageEvent } from '../pages/page';
import { CONFIG } from '../consts';

import States from '../helpers/states';
import Router, { RouterEvent } from '../helpers/router';
import Loader, { LoaderEvent } from '../helpers/loader';
import Google from '../helpers/google';

export default class Main {
    constructor() {
        this.Components = {};
        this.Page = null;

        this._cache = {};
        this._DOMparser = new DOMParser();
        this._isFirstInit = true;

        bindAll(this, [
            '_resize',
            '_routeChangeHandler',
            '_loadingHandler',
            '_mediaLoadedHandler',
            '_pageLoadedHandler',
            '_destroyClasses',
            '_request',
            '_response'
        ]);

        this._retrieveDom();
        this._bind();

        this._dom.body.classList.add('dom-content-loaded');

        if (CONFIG.DEBUG) {
            window.Main = this;
            window.States = States;
        }
    }

    /**
     * Retrieve DOM Elements
     */
    _retrieveDom() {
        this._dom = {
            body: document.body,
            main: document.querySelector('body main'),
            page: document.querySelector('body main .page')
        };

        States.dom = this._dom;
    }

    /**
     * Bind events
     */
    _bind() {
        window.addEventListener('resize', debounce(this._resize, 200));

        if (CONFIG.IS_AJAX)
            Router.on(RouterEvent.ROUTE_CHANGE, this._routeChangeHandler);
        Loader.on(LoaderEvent.LOADING, this._loadingHandler);
        if (CONFIG.IS_LAZY_LOADING)
            Loader.on(LoaderEvent.MEDIA_LOADED, this._mediaLoadedHandler);
        else
            Loader.on(LoaderEvent.MEDIA_LOADED, this._pageLoadedHandler);
    }

    /**
     * Initialise the current context
     */
    _initContext() {       
        this._retrieveDomProperties();
        this._initComponents();
        this._initPage();
        Loader.preloadMedia(this._isFirstInit ? this._dom.body : this._dom.page);
        if (CONFIG.IS_LAZY_LOADING)
            this._pageLoadedHandler();

        this._resize();

        Google.bind();
    }

    /**
     * Retrieve page properties: entity & page name
     */
    _retrieveDomProperties() {
        this._properties = {
            entity: this._dom.page.getAttribute('data-entity').toLowerCase(),
            page: this._dom.page.getAttribute('data-page').toLowerCase()
        };
    }

    /**
     * Detect available components in Page and instanciate them
     */
    _initComponents() {       
        const container = (this._isFirstInit) ? this._dom.body : this._dom.page;
        const components = container.querySelectorAll('[data-component]');
        for (let i = 0, j = components.length; i < j; i++) {
            const component = components[i];
            const name = component.getAttribute('data-component');
            if (this._map.components.hasOwnProperty(name)) {
                if (this.Components.hasOwnProperty(name.capitalize())) {
                    if (isArray(this.Components[name.capitalize()])) {
                        this.Components[name.capitalize()].push(new this._map.components[name].default(component));
                    } else {
                        this.Components[name.capitalize()] = [
                            this.Components[name.capitalize()],
                            new this._map.components[name].default(component)
                        ];
                    }
                } else {
                    this.Components[name.capitalize()] = new this._map.components[name].default(component);
                }
            } else {
                throw new Error(`Main${this._device.capitalize()} :: you are calling non-existing '${name}' component in your map file. Please check your map file.`);
            }
        }

        States.Components = this.Components;
    }

    /**
     * Detect current page, find class and instanciate it
     */
    _initPage() {       
        if (this._map.pages.hasOwnProperty(this._properties.entity)) {
            if (this._map.pages[this._properties.entity].hasOwnProperty(this._properties.page)) {
                this.Page = new this._map.pages[this._properties.entity][this._properties.page].default(this._dom.page, this.Components);
            } else {
                throw new Error(`Main${this._device.capitalize()} :: you are calling non-existing '${this._properties.page}' page for entity '${this._properties.page}' in your map file. Please check your map file.`);
            }
        } else {
            throw new Error(`Main${this._device.capitalize()} :: you are calling non-existing '${this._properties.entity}' page entity in your map file. Please check your map file.`);
        }

        States.Page = this.Page;
    }

    /**
     * Triggered when the window is resize
     */    
    _resize() {
        const width = window.innerWidth || document.documentElement.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight;

        this._browseComponents('resize', [width, height]);

        if (this.Page) this.Page.resize(width, height);

        States.width = width;
        States.height = height;
    }

    /**
     * Triggered when the route changes
     */
    _routeChangeHandler() {
        this.PreviousPage = this.Page;

        Router.lock();
        Google.unbind();

        this._browseComponents('unbind', null, (component, key) => {
            if (!component.persist) {
                component = null;
                delete this.Components[key];
            }
        });

        this.PreviousPage.unbind();
        this.PreviousPage.hide();

        this.PreviousPage.once(PageEvent.NEXT, this._request);
        this.PreviousPage.once(PageEvent.PAGE_DESTROYED, this._destroyClasses);
    }

    /**
     * Triggered when the media are loading
     */
    _loadingHandler(percent) {
        // console.log(percent);
    }

    /**
     * Triggered when the media are loaded
     */
    _mediaLoadedHandler() {
        this._browseComponents('mediaLoadedHandler');

        this.Page.mediaLoadedHandler();
    }

    /**
     * Triggered when the page is loaded
     */
    _pageLoadedHandler() {
        if (!CONFIG.IS_LAZY_LOADING)
            this._browseComponents('mediaLoadedHandler');
        this._browseComponents('init');
        this._browseComponents('bind');

        this.Page.init();
        this.Page.bind();
        this.Page.show();

        Router.unlock();
        Router.listen();
    }

    /**
     * Destroy instances of the previous Page / Components
     */
    _destroyClasses() {
        this._dom.main.removeChild(this.PreviousPage.dom.page);

        this.PreviousPage.off(PageEvent.PAGE_DESTROYED, this._destroyClasses);
        this.PreviousPage = null;
    }

    /**
     * AJAX request
     */
    _request() {
        Loader.show();

        const url = Router.getUrl();
        const cacheXhr = this._cache[url];

        if (cacheXhr) {
            this._response(cacheXhr);
        } else {
            axios.get(url)
                .then(response => {
                    this._response(response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        }

        this.PreviousPage.off(PageEvent.NEXT, this._request);
    }

    /**
     * AJAX response
     */
    _response(xhr) {
        this._cache[Router.getUrl()] = xhr;

        // Update DOM
        const dom = this._DOMparser.parseFromString(xhr, 'text/html');
        this._dom.page = dom.querySelector('div.page');
        this._dom.main.appendChild(this._dom.page);
        States.dom = this._dom;

        // Update title
        const title = dom.querySelector('title');
        Router.setTitle(title.innerText);

        Router.AJAXresponseHandler();
        Loader.AJAXresponseHandler();

        this._initContext();
    }

    _browseComponents(methodName, argsArray = [], callback = null) {
        for (let key in this.Components) {
            if (this.Components.hasOwnProperty(key)) {
                // Browse all components
                let component = null;
                if (isArray(this.Components[key])) {
                    for (let i = 0, j = this.Components[key].length; i < j; i++) {
                        const componentsType = this.Components[key];
                        component = componentsType[i];
                        if (component.persist === false || ((methodName !== 'init' || this._isFirstInit === true) && methodName !== 'unbind'))
                            component[methodName].apply(component, argsArray);
                    }
                } else {
                    component = this.Components[key];
                    if (component.persist === false || ((methodName !== 'init' || this._isFirstInit === true) && methodName !== 'unbind'))
                        component[methodName].apply(component, argsArray);
                }

                // Callback to call
                if (typeof callback === 'function')
                    callback.call(callback, component, key);
            }
        }

        if (methodName === 'init')
            this._isFirstInit = false;
    }
}



// WEBPACK FOOTER //
// ./src/js/main/main.js